import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { ModalHeader, ModalBody, ModalFooter } from '@/components/Modal'
import { Modal } from '@/screens/shared/ModalScrim'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { M_MFARESET_COPY } from '@/copy/modals'
import type { Problem } from '@/data/apiClient'

/**
 * `m-mfareset` — process/09-SCREEN-SPECS/m-mfareset.md. **`organisationResetUserFactor` now takes
 * a required `{ out_of_band_verified }` body** (reconciliation audit #35, `REQ-AUD-032`) — this
 * doc comment used to report that the contract had nowhere to put the checkbox's attestation; the
 * checkbox is no longer client-side-only, and the mutation below sends its exact value. The button
 * itself still gates on `verified` (M3, "states what it commits before the control is live"), so a
 * `false` never actually reaches the server today — but the body always carries the real value
 * rather than assuming it, since the server refuses `false`/missing with `VALIDATION_FAILED`
 * (REQ-AUD-032: no silent unverified reset) and that refusal should read as what it is.
 */
export function MMfareset({
  orgId,
  userId,
  userName,
  userEmail,
  onDismiss,
  onReset,
}: {
  orgId: string
  userId: string
  userName: string
  userEmail: string
  onDismiss: () => void
  onReset: () => void
}) {
  const [verified, setVerified] = useState(false)

  const reset = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.POST('/orgs/{org_id}/users/{user_id}/reset-mfa', {
        params: { path: { org_id: orgId, user_id: userId } },
        body: { out_of_band_verified: verified },
      })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: onReset,
  })

  const problem = reset.error instanceof ApiProblemError ? reset.error.problem : undefined
  const headingId = 'm-mfareset-heading'

  return (
    <Modal dataScr={unitId('m-mfareset')} labelledBy={headingId} onDismiss={onDismiss} maxWidth={420}>
      <ModalHeader id={headingId} heading={M_MFARESET_COPY.heading}>
        For {userName} — {userEmail}
      </ModalHeader>
      <ModalBody>
        {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <p className="text-fail text-body mb-6">{problem.title}</p>)}
        <Note>{M_MFARESET_COPY.removalNote}</Note>
        <div className="mt-6 flex flex-col gap-5">
          <Strip>{M_MFARESET_COPY.verifyFirstWarning}</Strip>
          <Strip>{M_MFARESET_COPY.auditNote}</Strip>
        </div>
        <label className="flex items-start gap-4 mt-6 text-body-lg">
          <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} />
          <span>{M_MFARESET_COPY.outOfBandLabel}</span>
        </label>
      </ModalBody>
      <ModalFooter>
        <Button data-act={undefined} ghost onClick={onDismiss}>
          {M_MFARESET_COPY.cancelButton}
        </Button>
        <Button data-act={actionId('user.resetFactor')} danger disabled={!verified || reset.isPending} onClick={() => reset.mutate()}>
          {M_MFARESET_COPY.resetButton}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
